import { createContext, useReducer } from 'react';
import { useRouter } from 'next/router';

import APClient from '@/lib/graphql/client';
import {
  addToCartMutation,
  applyCouponMutation,
  cleanCartMutation,
  removeCouponMutation,
  repeatOrderMutation,
  updateItemQuantitiesMutation,
  updateSubscriptionSchemaMutation,
} from '@/lib/graphql/mutations';
import { getCartQuery } from '@/lib/graphql/querys';
import { cartTransformer } from '@/lib/graphql/transformers';
import { routes } from '@/lib/routes';
import {
  getAddedOrUpdatedItemFromCart,
  gtmPush,
  normalizeEventItem,
} from '@/lib/utils';

import type { TypeCart } from '@/lib/graphql/types';
import type { ReactNode } from 'react';

enum CartAction {
  UPDATE_CART_CONTENT,
  UPDATE_CART_LOADING,
  UPDATE_CART_ERRORS,
}

type TypeCartAction =
  | {
      type: CartAction.UPDATE_CART_CONTENT;
      payload: {
        cart: TypeCart['cart'];
      };
    }
  | {
      type: CartAction.UPDATE_CART_LOADING;
      payload: {
        loading: boolean;
      };
    }
  | {
      type: CartAction.UPDATE_CART_ERRORS;
      payload: {
        errors: { message: string };
      };
    };

type TypeCartDispatch = {
  getCart: () => Promise<unknown>;
  add: (
    productId: number,
    quantity: number,
    extraData?: { [key: string]: string }[],
  ) => Promise<unknown>;
  update: (productId: string, quantity: number) => Promise<unknown>;
  remove: (productId: string) => Promise<unknown>;
  applyCoupon: (code: string) => Promise<unknown>;
  removeCoupon: (code: string) => Promise<unknown>;
  updateSubscriptionSchema: (schema: string) => Promise<unknown>;
  repeatOrder: (
    products: { quantity: number; productId: number }[],
  ) => Promise<unknown>;
  cleanCart: () => Promise<unknown>;
  setCartError: (message: string) => void;
};

const initialState: TypeCart = {
  cart: {
    appliedCoupons: [],
    availablePaymentMethods: [],
    availableShippingMethods: [],
    chosenShippingMethods: [],
    contents: {
      cart: [],
      edges: [],
      isMixedCart: false,
      itemCount: 0,
      products: [],
      typeOfPurchase: {
        discountCart: 0,
        isOnlySuscribable: false,
        isSubscription: false,
        onlySuscriptionPrice: 0,
        subscriptionPrice: 0,
        total: 0,
        totalDiscount: 0,
        uniquePrice: 0,
        withoutSubscription: 0,
      },
    },
    contentsTax: '0,000€',
    contentsTotal: '0,000€',
    discountTax: '0,000€',
    discountTotal: '0,000€',
    displayPricesIncludeTax: true,
    errors: undefined,
    feeTax: '0,000€',
    feeTotal: '0,000€',
    shippingDates: [],
    shippingTax: '0,000€',
    shippingTotal: '0,000€',
    subscriptionData: {
      discount: 0,
      discount_cart: 0,
      hasSuscription: '',
      recurring_purchase: 0,
      single_purchase: 0,
      subscription: 0,
      total: 0,
      total_discount: 0,
    },
    subtotal: '0,000€',
    subtotalTax: '0,000€',
    total: '0,000€',
    totalTax: '0,000€',
    warnings: undefined,
  },
  cartError: { message: '' },
  loading: true,
};

export const CartContext = createContext<TypeCart>(initialState);
export const CartDispatchContext = createContext<TypeCartDispatch>({
  add: async () => null,
  update: async () => null,
  remove: async () => null,
  applyCoupon: async () => null,
  removeCoupon: async () => null,
  updateSubscriptionSchema: async () => null,
  repeatOrder: async () => null,
  cleanCart: async () => null,
  setCartError: () => null,
  getCart: async () => null,
});

const reducer = (cart: TypeCart, action: TypeCartAction): TypeCart => {
  switch (action.type) {
    case CartAction.UPDATE_CART_CONTENT:
      return {
        ...cart,
        cart: action.payload.cart,
      };
    case CartAction.UPDATE_CART_LOADING:
      return {
        ...cart,
        loading: action.payload.loading,
      };
    case CartAction.UPDATE_CART_ERRORS:
      return {
        ...cart,
        cartError: {
          message: action.payload.errors.message,
        },
      };
    default: {
      throw Error(`Tipo de action desconocida: ${action}`);
    }
  }
};

export const CartProvider = ({
  children,
}: {
  children: ReactNode | ReactNode[];
}) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const router = useRouter();

  const setCartError = (message: string) => {
    dispatch({
      type: CartAction.UPDATE_CART_ERRORS,
      payload: {
        errors: {
          message: message,
        },
      },
    });
  };

  const add: TypeCartDispatch['add'] = async (productID, quantity, extraData) =>
    new Promise((resolve, reject) => {
      APClient.mutate({
        mutation: addToCartMutation,
        variables: {
          productID: productID,
          quantity: quantity,
          extraData: JSON.stringify(extraData),
        },
      })
        .then((value) => {
          const cart: TypeCart['cart'] = cartTransformer(
            value.data.addToCart.cart,
          );

          if (cart?.errors || cart?.warnings) {
            setCartError((cart.errors || cart.warnings) ?? '');
            reject(cart.errors || cart.warnings);
          }

          dispatch({
            type: CartAction.UPDATE_CART_CONTENT,
            payload: {
              cart: cart,
            },
          });

          const addedItem = getAddedOrUpdatedItemFromCart(
            productID as any,
            undefined as any,
            cart,
          );

          if (addedItem.data) {
            gtmPush({
              event: 'add_to_cart',
              ecommerce: {
                items: [
                  normalizeEventItem(
                    addedItem.data.product,
                    addedItem.itemIndex,
                    addedItem.data.quantity,
                    addedItem.data.variation,
                  ),
                ],
              },
            });
          }

          setCartError('');
          resolve(cart);
        })
        .catch((error) => {
          setCartError(error.message);
          reject(error);
        });
    });

  const update: TypeCartDispatch['update'] = async (productId, quantity) =>
    new Promise((resolve, reject) => {
      APClient.mutate({
        mutation: updateItemQuantitiesMutation,
        variables: {
          key: productId,
          quantity: quantity,
        },
      })
        .then((value) => {
          const oldCart: TypeCart['cart'] = structuredClone(state.cart);

          const newCart: TypeCart['cart'] = cartTransformer(
            value.data.updateItemQuantities.cart,
          );

          if (newCart?.errors || newCart?.warnings) {
            setCartError((newCart?.errors ?? '') || (newCart?.warnings ?? ''));
            reject(newCart.errors || newCart.warnings);
          }

          dispatch({
            type: CartAction.UPDATE_CART_CONTENT,
            payload: {
              cart: newCart,
            },
          });

          const updatedItem = getAddedOrUpdatedItemFromCart(
            undefined as any,
            productId as any,
            quantity !== 0 ? newCart : oldCart,
          );

          if (updatedItem.data) {
            gtmPush({
              event: `${
                quantity === 0
                  ? 'remove_from_cart'
                  : (oldCart?.contents.itemCount ?? 0) <
                    (newCart?.contents.itemCount ?? 0)
                  ? 'add_to_cart'
                  : 'remove_from_cart'
              }`,
              ecommerce: {
                items: [
                  normalizeEventItem(
                    updatedItem.data.product,
                    updatedItem.itemIndex,
                    updatedItem.data.quantity,
                    updatedItem.data.variation,
                  ),
                ],
              },
            });
          }

          setCartError('');
          resolve(newCart);
        })
        .catch((error) => {
          setCartError(error.message);
          reject(error);
        });
    });

  const remove: TypeCartDispatch['remove'] = async (productId) => {
    await update(productId, 0);
  };

  const applyCoupon: TypeCartDispatch['applyCoupon'] = async (code) =>
    new Promise((resolve, reject) => {
      APClient.mutate({
        mutation: applyCouponMutation,
        variables: {
          code: code,
        },
      })
        .then((value) => {
          const cart: TypeCart['cart'] = cartTransformer(
            value.data.applyCoupon.cart,
          );

          if (cart?.errors || cart?.warnings) {
            setCartError((cart.errors || cart.warnings) ?? '');
            reject(cart.errors || cart.warnings);
          }

          dispatch({
            type: CartAction.UPDATE_CART_CONTENT,
            payload: {
              cart: cart,
            },
          });

          setCartError('');
          resolve(cart);
        })
        .catch((error) => {
          setCartError(error.message);
          reject(error);
        });
    });

  const removeCoupon: TypeCartDispatch['removeCoupon'] = async (code) =>
    new Promise((resolve, reject) => {
      APClient.mutate({
        mutation: removeCouponMutation,
        variables: {
          code: code,
        },
      })
        .then((value) => {
          const cart: TypeCart['cart'] = cartTransformer(
            value.data.removeCoupons.cart,
          );

          if (cart?.errors || cart?.warnings) {
            setCartError((cart.errors || cart.warnings) ?? '');
            reject(cart.errors || cart.warnings);
          }

          dispatch({
            type: CartAction.UPDATE_CART_CONTENT,
            payload: {
              cart: cart,
            },
          });

          setCartError('');
          resolve(cart);
        })
        .catch((error) => {
          setCartError(error.message);
          reject(error);
        });
    });

  const updateSubscriptionSchema: TypeCartDispatch['updateSubscriptionSchema'] =
    async (schema) =>
      new Promise((resolve, reject) => {
        APClient.mutate({
          mutation: updateSubscriptionSchemaMutation,
          variables: {
            schema: schema,
          },
        })
          .then((value) => {
            const cart: TypeCart['cart'] = cartTransformer(
              value.data.updateSubscriptionSchema.cart,
            );

            if (cart?.errors || cart?.warnings) {
              setCartError((cart.errors || cart.warnings) ?? '');
              reject(cart.errors || cart.warnings);
            }

            dispatch({
              type: CartAction.UPDATE_CART_CONTENT,
              payload: {
                cart: cart,
              },
            });

            setCartError('');
            resolve(cart);
          })
          .catch((error) => {
            setCartError(error.message);
            reject(error);
          });
      });

  const cleanCart: TypeCartDispatch['cleanCart'] = async () =>
    new Promise((resolve, reject) => {
      APClient.mutate({
        mutation: cleanCartMutation,
      })
        .then((value) => {
          const cart: TypeCart['cart'] = cartTransformer(
            value.data.emptyCart.cart,
          );

          if (cart?.errors || cart?.warnings) {
            setCartError((cart.errors || cart.warnings) ?? '');
            reject(cart.errors || cart.warnings);
          }

          dispatch({
            type: CartAction.UPDATE_CART_CONTENT,
            payload: {
              cart: cart,
            },
          });

          setCartError('');
          resolve(cart);
        })
        .catch((error) => {
          setCartError(error.message);
          reject(error);
        });
    });

  const repeatOrder: TypeCartDispatch['repeatOrder'] = async (products) =>
    new Promise((resolve, reject) => {
      if (products.length === 0) {
        setCartError('No se han podido añadir los artículos a la cesta');
        reject('No se han podido añadir los artículos a la cesta');
      }

      const repeatOrder = () => {
        APClient.mutate({
          mutation: repeatOrderMutation,
          variables: {
            items: products,
          },
        })
          .then((value) => {
            const cart: TypeCart['cart'] = cartTransformer(
              value.data.fillCart.cart,
            );

            if (cart?.errors || cart?.warnings) {
              setCartError((cart.errors || cart.warnings) ?? '');
              reject(cart.errors || cart.warnings);
            }

            dispatch({
              type: CartAction.UPDATE_CART_CONTENT,
              payload: {
                cart: cart,
              },
            });

            router.push(routes.cart);
            setCartError('');
            resolve(cart);
          })
          .catch((error) => {
            setCartError(error.message);
            reject(error);
          });
      };

      if (state.cart.contents.itemCount > 0) {
        cleanCart()
          .then(repeatOrder)
          .catch((error) => {
            if (error.message === 'Cart is empty') {
              repeatOrder();
            } else {
              setCartError(error);
              reject(error);
            }
          });
      } else {
        repeatOrder();
      }
    });

  const getCart: TypeCartDispatch['getCart'] = async () => {
    dispatch({
      type: CartAction.UPDATE_CART_LOADING,
      payload: { loading: true },
    });
    await APClient.query({
      query: getCartQuery,
    })
      .then((value) => {
        const cart: TypeCart['cart'] = cartTransformer(value.data.cart);

        dispatch({
          type: CartAction.UPDATE_CART_CONTENT,
          payload: {
            cart: cart,
          },
        });
      })
      .finally(() => {
        dispatch({
          type: CartAction.UPDATE_CART_LOADING,
          payload: { loading: false },
        });
      });
  };

  return (
    <CartContext.Provider value={state}>
      <CartDispatchContext.Provider
        value={{
          add,
          remove,
          update,
          applyCoupon,
          removeCoupon,
          updateSubscriptionSchema,
          repeatOrder,
          cleanCart,
          setCartError,
          getCart,
        }}
      >
        {children}
      </CartDispatchContext.Provider>
    </CartContext.Provider>
  );
};
